import { useTranslation } from "react-i18next";
import React from "react";
import { clsx } from "clsx";
import {
  CheckCircleIcon,
  CubeIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import useIntegration from "../utils/useIntegration";
const getTypeStyles = (type) => {
  const base =
    "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border transition-colors duration-300";
  switch (type?.toLowerCase()) {
    case "production":
      return clsx(
        base,
        "bg-emerald-50 text-emerald-600 border-emerald-100 group-hover:bg-emerald-100",
      );
    case "sandbox":
    case "test":
      return clsx(
        base,
        "bg-amber-50 text-amber-600 border-amber-100 group-hover:bg-amber-100",
      );
    case "staging":
      return clsx(
        base,
        "bg-blue-50 text-blue-600 border-blue-100 group-hover:bg-blue-100",
      );
    default:
      return clsx(
        base,
        "bg-slate-50 text-slate-500 border-slate-100 group-hover:bg-slate-100",
      );
  }
};
const LoadingSkeleton = () => (
  <div className="pb-4 space-y-3 px-1">
    <div className="h-3 w-20 bg-slate-100 rounded-full animate-pulse ms-3 mb-4"></div>
    {[1, 2].map((i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50/50 border border-slate-100 animate-pulse"
      >
        <div className="h-10 w-10 bg-slate-200 rounded-xl"></div>
        <div className="flex-1 space-y-2">
          <div className="h-3 w-24 bg-slate-200 rounded"></div>
          <div className="h-2 w-12 bg-slate-100 rounded"></div>
        </div>
      </div>
    ))}
  </div>
);
function IntegrationSelector() {
  const { t } = useTranslation();
  const { integrationList, integration, setIntegration, isLoading } =
    useIntegration();
  if (isLoading) return <LoadingSkeleton />;
  if (!integrationList?.length) return null;
  return (
    <div className="pb-6">
      <div className="px-3 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Squares2X2Icon className="h-3.5 w-3.5 text-primary-500/70" />
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
            {t("Integrations")}
          </p>
        </div>
        <span className="text-[10px] font-medium text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded-md border border-slate-100/50">
          {integrationList.length}
        </span>
      </div>

      <div className="space-y-2">
        {integrationList.map((item) => {
          const isActive =
            item.integrationUuid === integration?.integrationUuid;
          return (
            <button
              key={item.integrationUuid}
              onClick={() => setIntegration(item)}
              className={clsx(
                "group relative w-full flex flex-col items-start gap-3 rounded-2xl p-4 transition-all duration-300",
                "border text-start",
                isActive
                  ? "bg-white border-primary-200 shadow-lg shadow-primary-500/5 ring-1 ring-primary-500/10 translate-x-1"
                  : "bg-slate-50/40 border-slate-200/50 hover:bg-white hover:border-slate-300 hover:shadow-md hover:shadow-slate-200/50",
              )}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-500",
                      isActive
                        ? "bg-primary-500 text-white shadow-lg shadow-primary-500/20 rotate-0 scale-100"
                        : "bg-white text-slate-400 border border-slate-200 group-hover:border-primary-200 group-hover:text-primary-500 group-hover:scale-105 group-hover:-rotate-3",
                    )}
                  >
                    <CubeIcon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex flex-col">
                    <span
                      className={clsx(
                        "truncate text-sm font-bold transition-colors duration-300",
                        isActive
                          ? "text-slate-900"
                          : "text-slate-600 group-hover:text-slate-900",
                      )}
                    >
                      {item.name}
                    </span>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={getTypeStyles(item.integrationType)}>
                        {item.integrationType}
                      </span>
                    </div>
                  </div>
                </div>

                {isActive && (
                  <div className="absolute top-4 end-4 transition-all duration-300 scale-110">
                    <CheckCircleIcon className="h-5 w-5 text-primary-500" />
                  </div>
                )}
              </div>

              {/* Decorative element for active state */}
              {isActive && (
                <div className="absolute start-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary-500 rounded-e-full" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
export default IntegrationSelector;
